'use client'

import { useCallback, useMemo } from 'react'
import {
  cn,
  resolveCatchMessage,
  setUserSubscription,
  storeConfigSelector,
  TCatchMessage,
  useGetFormilyBlockQuery,
  useLoadingContext,
  useMediaQuery,
  userInfoSelector,
  userIsSubscribedSelector,
  useSubscribeEmailMutation,
  useToastContext,
  useUpdateCustomerV2Mutation,
} from '@segway/core'
import { useAppDispatch, useAppSelector } from '@segway/core/src/store/hooks'
import { useToggle } from 'ahooks'

import { Dialog } from '@/components'
import { Button, Skeleton } from '@/ui'
import reportGTMEvent from '@/utils/gtm'

import { SubscriptionCouponCode } from '../subscriptionCouponCode'

type CouponAndSubscriptionInfo = {
  coupon_title: string
  coupon_amount: string
  subscribe_title: string
  subscribe_subtitle: string
  subscribe_content: string
  unsubscribe_title: string
  unsubscribe_content: string
}

const SubScription = () => {
  const [isUnsubscribeDialogVisible, { toggle: handleToggleUnsubscribeConfirmationDialog }] =
    useToggle(false)

  const response = useMediaQuery()
  const dispatch = useAppDispatch()

  const isSubscribed = useAppSelector(userIsSubscribedSelector)
  const userInfo = useAppSelector(userInfoSelector)
  const storeConfig = useAppSelector(storeConfigSelector)

  const userEmail = userInfo?.email
  const isFetchUserInfoLoading = userInfo?.isFetchUserInfoLoading

  const loading = useLoadingContext()
  const toast = useToastContext()

  const { data, isLoading } = useGetFormilyBlockQuery({ identify: 'subscribe_collection' })
  const [subscribeEmail] = useSubscribeEmailMutation()
  const [updateCustomerV2] = useUpdateCustomerV2Mutation()

  const handleEmailSubscription = useCallback(async () => {
    if (!userEmail) {
      toast.show({
        icon: 'fail',
        content: 'Can not find user email',
      })

      return
    }

    try {
      loading.show()
      const resp = await subscribeEmail({
        email: userEmail,
      }).unwrap()

      if (resp && resp.subscribeEmailToNewsletter?.status) {
        reportGTMEvent({
          event: 'generate_lead',
          ecommerce: {
            currency: storeConfig?.default_display_currency_code || '',
            value: '20.00', // 固定写死 20
            lead_source: 'myaccount_signup',
          },
        })
        dispatch(setUserSubscription(true))
        toast.show({
          icon: 'success',
          content: 'Suscripción exitosa',
        })
      }
    } catch (error) {
      toast.show({
        icon: 'fail',
        content: resolveCatchMessage(error as TCatchMessage),
      })
    } finally {
      loading.hide()
    }
  }, [subscribeEmail, dispatch, loading, toast, userEmail, storeConfig])

  const handleEmainUnsubscribe = async () => {
    try {
      loading.show()

      const response = await updateCustomerV2({
        input: {
          is_subscribed: false,
        },
      }).unwrap()

      if (response?.updateCustomerV2?.customer) {
        dispatch(setUserSubscription(false))
        toast.show({
          icon: 'success',
          content: 'darse de baja con éxito',
        })
        handleToggleUnsubscribeConfirmationDialog()
      }
    } catch (error) {
      toast.show({
        icon: 'fail',
        content: resolveCatchMessage(error as TCatchMessage),
      })
    } finally {
      loading.hide()
    }
  }

  const subscriptionBlockData = useMemo(() => {
    if (data && data.formilyBlock) {
      const subscribeBlock = data.formilyBlock.block_data

      if (!subscribeBlock) {
        return {
          coupon_title: '',
          coupon_amount: '',
          subscribe_title: '',
          subscribe_subtitle: '',
          subscribe_content: '',
          unsubscribe_title: '',
          unsubscribe_content: '',
        }
      }

      const accountSubscribeBlockData: CouponAndSubscriptionInfo =
        JSON.parse(subscribeBlock)?.account_center

      return accountSubscribeBlockData
    }
  }, [data])

  return (
    <>
      {isLoading || isFetchUserInfoLoading ? (
        <>
          {isSubscribed ? (
            <div className="flex flex-col-reverse items-center justify-between gap-8 rounded-[20px] bg-white px-[31px] py-[24px] xl:flex-row">
              <div className="flex w-full flex-col items-center gap-[8px] xl:items-start">
                <Skeleton className="h-7 w-[100px]" />
                <Skeleton className="h-7 w-full xl:w-[500px]" />

                <Skeleton className="h-[40px] w-full rounded-[18px] xl:h-[32px] xl:w-[100px]" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col-reverse items-center justify-between gap-8 rounded-[20px] bg-white px-[31px] py-[24px] xl:flex-row">
              <div className="flex w-full flex-col items-center gap-[8px] xl:items-start">
                <Skeleton className="h-7 w-[100px]" />
                <Skeleton className="h-7 w-full xl:w-[500px]" />
                <Skeleton className="h-7 w-full xl:w-[600px]" />

                <Skeleton className="h-[40px] w-full rounded-[18px] xl:h-[32px] xl:w-[100px]" />
              </div>

              <div className="mr-[10px] flex w-full justify-center md:w-1/3 xl:justify-end">
                <Skeleton className="h-[80px] w-full rounded-[8px] xl:w-[200px]" />
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {isSubscribed ? (
            <div className="flex flex-col-reverse items-center justify-between gap-8 rounded-[20px] bg-white px-[24px] py-[24px] xl:flex-row xl:px-[31px]">
              <div className="w-full space-y-[12px] text-center md:w-2/3 xl:space-y-[8px] xl:text-start">
                {subscriptionBlockData?.subscribe_title && (
                  <h2 className="font-miSansDemiBold450 text-[18px] text-[#000000] text-[450]">
                    {subscriptionBlockData.subscribe_title}
                  </h2>
                )}
                {subscriptionBlockData?.unsubscribe_content && (
                  <p className="px-7 font-miSansMedium380 text-sm leading-[1.4] text-[#6E6E73] xl:px-0 xl:font-miSansRegular330 xl:text-lg xl:leading-[1.6]">
                    {subscriptionBlockData?.unsubscribe_content}
                  </p>
                )}

                <Button
                  type="submit"
                  onClick={handleToggleUnsubscribeConfirmationDialog}
                  variant="ghost"
                  className={cn(
                    '!mt-[24px] h-[40px] rounded-full border border-solid border-[#BBBBBD] px-4 py-2 font-miSansMedium380 text-base text-[#000000] xl:!mt-[16px] xl:h-[32px]',
                    response && response.xl ? '' : 'w-full',
                  )}>
                  Unsubscribe
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col-reverse items-center justify-between gap-[18px] rounded-[20px] bg-white px-[31px] py-[24px] xl:flex-row">
              <div className="w-full text-center md:w-2/3 xl:text-start">
                {subscriptionBlockData?.subscribe_title && (
                  <h2 className="mb-[12px] font-miSansDemiBold450 text-[18px] text-[#000000] text-[450] xl:mb-[8px]">
                    {subscriptionBlockData.subscribe_title}
                  </h2>
                )}

                {subscriptionBlockData?.subscribe_subtitle && (
                  <div
                    className="font-miSansRegular330 text-lg leading-normal text-[#6E6E73] xl:leading-[1.6]"
                    dangerouslySetInnerHTML={{
                      __html: subscriptionBlockData?.subscribe_subtitle,
                    }}
                  />
                )}

                {subscriptionBlockData?.subscribe_content && (
                  <p className="font-miSansRegular330 text-lg leading-normal text-[#6E6E73] xl:leading-[1.6]">
                    {subscriptionBlockData?.subscribe_content}
                  </p>
                )}

                <Button
                  onClick={handleEmailSubscription}
                  type="submit"
                  variant="default"
                  className={cn(
                    '!mt-[24px] h-[40px] rounded-full px-4 py-2 font-miSansMedium380 text-base text-white xl:!mt-[16px] xl:h-[32px]',
                    response && response.xl ? '' : 'w-full',
                  )}>
                  Suscripción
                </Button>
              </div>

              {subscriptionBlockData?.coupon_amount && subscriptionBlockData?.coupon_title && (
                <div className="mr-[10px] flex w-full justify-center md:w-1/3 xl:justify-end">
                  <SubscriptionCouponCode
                    amount={subscriptionBlockData?.coupon_amount}
                    text={subscriptionBlockData?.coupon_title}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
      <Dialog
        hasCloseIcon={false}
        open={isUnsubscribeDialogVisible}
        onOpenChange={handleToggleUnsubscribeConfirmationDialog}
        containerStyle="w-[311px] xl:w-[592px] rounded-lg"
        titleStyle="text-[#0F0F0F] text-start font-miSansDemiBold450 font-[450] text-lg xl:font-miSansDemiBold450 xl:text-2xl"
        title="¿Cancelar la suscripción al boletín de Segway?"
        footer={
          <>
            {response && response.xl ? (
              <div className="flex items-center justify-end space-x-4">
                <Button
                  onClick={handleEmainUnsubscribe}
                  className="rounded-full bg-gray-0 px-6 py-2 text-[#0F0F0F] hover:bg-gray-0 focus:bg-gray-0 focus:text-black active:bg-gray-0 active:text-black">
                  Darse de baja
                </Button>
                <Button onClick={handleToggleUnsubscribeConfirmationDialog}>Cancel</Button>
              </div>
            ) : (
              <div className="mt-[12px] flex w-full flex-col items-center gap-[11px]">
                <Button className="w-full" onClick={handleToggleUnsubscribeConfirmationDialog}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleEmainUnsubscribe}
                  className="w-full rounded-full bg-gray-0 px-6 py-2 text-[#0F0F0F] hover:bg-gray-0 focus:bg-gray-0 focus:text-black active:bg-gray-0 active:text-black">
                  Darse de baja
                </Button>
              </div>
            )}
          </>
        }>
        <div className="py-[12px] font-miSansRegular330 text-base text-[#444446] xl:py-[24px] xl:font-miSansMedium380 xl:text-xl">
          ¿Seguro que quieres darte de baja? Dejarás de recibir novedades sobre productos y ofertas
          exclusivas.
        </div>
      </Dialog>
    </>
  )
}

export default SubScription
